import { useMemo } from 'react'
import {
  faSquareArrowUpRight,
  faLandmark,
  faHome,
} from '@fortawesome/pro-regular-svg-icons'
import { Badge, BadgeColor, Icon, Link, Text } from '@/ui'
import { mtc, TypographyStyles } from '@/styles'
import { DeadlineLabel } from '../DeadlineLabel'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { splitTaxYearQuarter } from '../../helpers'
import {
  selectAllQTEChecklistItemsCompletedOnDate,
  selectStatusForTaxEstimate,
  selectUserTaxEstimateById,
} from '../../userTaxEstimates.selector'
import { EstimateStatus } from '../../service'
import { createLinkToTaxBreakdown } from './TaxEstimateCta'
import { useBreakdownTransparency } from '../estimate-breakdown/useBreakdownTransparency'
import {
  BreakdownVersion,
  selectCalculatorVersionByTaxEstimateId,
} from '@/selectors/userTaxCalculation.selectors'
import { WhatIsSafeHarborPopup } from '../calculate-safe-harbor-flow/helpers'
import { selectActiveQTEDetailsAsDates } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const HowMuchShouldIPayRow = ({
  readyForPayment,
}: {
  readyForPayment?: boolean
}) => (
  <div className="mb-4">
    <Link
      href={
        readyForPayment
          ? 'https://support.joinheard.com/hc/en-us/articles/4416196188439-What-do-you-mean-by-estimate-Can-I-pay-more-or-less-than-the-estimate-you-gave-me'
          : 'https://support.joinheard.com/hc/en-us/articles/22050337435415-How-to-manually-calculate-quarterly-estimate-tax'
      }
      newPage
    >
      How much should I pay?
    </Link>
  </div>
)

export const LeftColumn = ({ estimateId }: { estimateId?: number }) => {
  const estimate = useReselector(selectUserTaxEstimateById, estimateId)
  const status = useReselector(selectStatusForTaxEstimate, estimateId)
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)
  const checklistCompletedAt = useReselector(
    selectAllQTEChecklistItemsCompletedOnDate,
    activeQuarterDetails?.taxYear
  )
  const hasBreakdownPage =
    useReselector(selectCalculatorVersionByTaxEstimateId, estimateId) !==
    BreakdownVersion.defaultBreakdown
  const isFederal = estimate?.type === 'federal'
  const { quarter } = splitTaxYearQuarter(estimate?.taxQuarter)

  const showQ3CaliforniaExplainer =
    quarter === '3' && estimate?.filingState === 'CA'
  const transparencyEnabled = useBreakdownTransparency()

  const howWasThisCalculatedRow = useMemo(() => {
    if (!isFederal || !hasBreakdownPage) {
      return (
        <Link
          href={createLinkToTaxBreakdown(estimate ?? null)}
          newPage={false}
          className="mb-4"
        >
          {isFederal
            ? 'How was this calculated?'
            : transparencyEnabled
              ? `See how your ${estimate?.filingState} estimate was calculated`
              : 'What does Heard include in state estimate calculations?'}
        </Link>
      )
    }
    return null
  }, [estimate, isFederal, hasBreakdownPage, transparencyEnabled])

  const deadlineLabelText = useMemo(() => {
    if (status !== EstimateStatus.awaitingEstimate || !activeQuarterDetails)
      return ''

    const { withOffset, forDisplay } = activeQuarterDetails
    if (checklistCompletedAt) {
      if (checklistCompletedAt < withOffset.initialCalculationLock) {
        return `Estimate ready by ${forDisplay.clientNotified.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}`
      } else if (checklistCompletedAt < withOffset.clientNotified) {
        return `Estimate ready after ${forDisplay.clientNotified.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}`
      } else if (checklistCompletedAt < withOffset.aFewDaysBeforePaymentDue) {
        return `Estimate ready before ${forDisplay.irsPaymentDue.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}`
      } else {
        return `Estimate ready after ${forDisplay.irsPaymentDue.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}`
      }
    }
    return 'Complete your checklist to receive your estimate'
  }, [activeQuarterDetails, checklistCompletedAt, status])

  const label = useMemo(() => {
    let color: BadgeColor
    let text

    switch (status) {
      case EstimateStatus.upcoming:
        color = 'olive'
        text = 'Upcoming'
        break
      case EstimateStatus.unpaidWithoutResponse:
        color = 'red'
        text = 'Not Updated'
        break
      case EstimateStatus.awaitingEstimate:
        color = 'yellow'
        text = 'Awaiting Estimate'
        break
      case EstimateStatus.paid:
        color = 'green'
        text = 'Paid'
        break
      case EstimateStatus.readyForPayment:
        color = 'orange'
        text = 'Ready for Payment'
        break
      case EstimateStatus.unpaidWithResponse:
        color = 'gray'
        text = 'Unpaid'
        break
      case EstimateStatus.noIncomeTax:
      case EstimateStatus.estimateIsZero:
      case EstimateStatus.joinedTooLate:
        return null
      default:
        return status satisfies never
    }

    return <Badge color={color as BadgeColor}>{text}</Badge>
  }, [status])

  const content = useMemo(() => {
    if (estimate?.safeHarborType) {
      return (
        <div className="mb-4">
          <WhatIsSafeHarborPopup />
        </div>
      )
    }

    switch (status) {
      case EstimateStatus.awaitingEstimate:
        return (
          <div className="mb-4">
            <DeadlineLabel text={deadlineLabelText} prominentText={null} />
          </div>
        )
      case EstimateStatus.readyForPayment:
        return (
          <>
            <HowMuchShouldIPayRow readyForPayment />
            {howWasThisCalculatedRow}
          </>
        )
      case EstimateStatus.noIncomeTax:
        return (
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/4416208081943-Why-don-t-certain-states-have-to-submit-state-quarterly-tax-payments-Why-not-"
            newPage
            className="mb-4"
          >
            Why don&apos;t I owe taxes?
          </Link>
        )
      case EstimateStatus.estimateIsZero: {
        if (showQ3CaliforniaExplainer) {
          return (
            <>
              <Text variant={TypographyStyles.BODY_SM} className="mb-4">
                <b>You&apos;re all set! No action is needed.</b>
                <br />
                Nothing is due for Q3 in CA. Please note that the percent per
                quarter for CA differs from federal.
              </Text>
              <Link
                href="https://www.ftb.ca.gov/about-ftb/newsroom/tax-news/august-2018/third-quarter-estimated-tax-due.html"
                newPage
                className="mb-4"
              >
                Learn more on CA.gov
                <Icon className="ml-1" icon={faSquareArrowUpRight} />
              </Link>
              {howWasThisCalculatedRow}
            </>
          )
        }

        return (
          <>
            <Text variant={TypographyStyles.BODY_SM} className="mb-4">
              <b>You&apos;re all set! No action is needed.</b>
            </Text>
            <Link
              href="https://support.joinheard.com/hc/en-us/articles/4416208000791-Why-is-my-quarterly-tax-estimate-0-"
              newPage
              className="mb-4"
            >
              Why don&apos;t I owe anything?
            </Link>
            <br />
            {howWasThisCalculatedRow}
          </>
        )
      }
      case EstimateStatus.unpaidWithResponse:
        return (
          <>
            <Text variant={TypographyStyles.BODY_SM} className="mb-4">
              You&apos;ve indicated that you didn&apos;t pay taxes this quarter.
            </Text>
            {howWasThisCalculatedRow}
          </>
        )
      case EstimateStatus.unpaidWithoutResponse:
        return (
          <>
            <Text variant={TypographyStyles.BODY_SM} className="mb-4">
              <b>
                Please update your payment, even if you didn&apos;t make any
                payments in Q{quarter}.
              </b>
              <br />
              We need this to provide accurate estimates for the next quarter.
            </Text>
            <HowMuchShouldIPayRow />
            {howWasThisCalculatedRow}
          </>
        )
      case EstimateStatus.joinedTooLate:
        return (
          <Text variant={TypographyStyles.BODY_SM} className="mb-4">
            You joined Heard after this quarter&apos;s cut-off date, so
            we&apos;re unable to provide you with an exact estimate.
          </Text>
        )
      case EstimateStatus.paid:
        return howWasThisCalculatedRow
      case EstimateStatus.upcoming:
        return null
      default:
        return status satisfies never
    }
  }, [
    deadlineLabelText,
    estimate?.safeHarborType,
    howWasThisCalculatedRow,
    quarter,
    showQ3CaliforniaExplainer,
    status,
  ])

  const titleText = useMemo(() => {
    if (estimate?.safeHarborType) return 'Federal Safe Harbor'
    if (isFederal) return 'Federal'
    return `State, ${estimate?.filingState}`
  }, [estimate?.filingState, estimate?.safeHarborType, isFederal])

  return (
    <div className="w-full sm:w-2/3">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Icon
            backgroundStyle="rounded"
            icon={isFederal ? faLandmark : faHome}
            size="sm"
            color="text-neutral-0"
            backgroundClassName={mtc(
              status === EstimateStatus.unpaidWithoutResponse
                ? 'bg-red-200'
                : isFederal
                  ? 'bg-green-200'
                  : 'bg-neutral-500'
            )}
          />
          <Text
            variant={TypographyStyles.H3}
            className={mtc(
              'mx-2',
              status === EstimateStatus.unpaidWithoutResponse
                ? 'text-red-600'
                : 'text-neutral-700'
            )}
          >
            {titleText}
          </Text>
          {label}
        </div>
        {content}
      </div>
    </div>
  )
}
